import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';
import { fetchCarrierRouteStats, type CarrierRouteStats } from '../lib/api';

interface CarrierFiberRouteDetailProps {
  carrier: string;
  onClose: () => void;
}

// Static carrier profiles — data that isn't in our DB but is relevant for BTM context.
// Keep concise; no invented numbers.
const CARRIER_PROFILES: Record<string, {
  color: string;
  tier: string;
  description: string;
  sourceName: string;
  sourceUrl?: string;
  sourceCaveat: string;
  btmNote: string;
  wholesale: string;
}> = {
  FiberLight: {
    color: '#f59e0b',
    tier: 'Regional backbone',
    description:
      'FiberLight is a regional fiber carrier with a deep Texas / Permian footprint and a national backbone segment. They sell dark fiber, wavelengths, and Ethernet transport; the Permian network is marketed directly to oil & gas and data center customers.',
    sourceName: 'FiberLight Marketing Map (ArcGIS FeatureServer)',
    sourceUrl: 'https://fiberlight.com',
    sourceCaveat:
      'The public ArcGIS source went private on 2026-04-11. Our data is a frozen snapshot from an earlier ingest and may not reflect new builds after that date.',
    btmNote:
      'FiberLight dominates the Permian long-haul corridor in our data — for sites in Pecos, Reeves, Ward, Loving, and Ector counties, FiberLight is typically the nearest backbone. Worth contacting early for buildout quotes.',
    wholesale: 'Contact: wholesale@fiberlight.com · fiberlight.com',
  },
  Zayo: {
    color: '#0891b2',
    tier: 'Tier-1 long-haul',
    description:
      'Zayo is a Tier-1 long-haul backbone carrier with one of the largest fiber footprints in North America. Their product suite includes dark fiber, wavelengths, Ethernet, IP transit, and carrier-neutral colocation interconnect.',
    sourceName: 'ZayoAllFiber ArcGIS FeatureServer',
    sourceUrl: 'https://www.zayo.com/solutions/global-network/',
    sourceCaveat:
      'Owner org appears to be Zayo\u2019s corporate Esri tenant and is actively maintained, but the hosted item could flip private at any time (the way FiberLight did). Our data should be treated as a point-in-time snapshot, not a live feed.',
    btmNote:
      'Zayo adds real multi-carrier redundancy to most TX parcels. In the Permian heart (Pecos, Reeves) Zayo coverage is sparse and the nearest backbone is usually FiberLight; around Abilene, Midland\u2013Odessa, DFW, and Dallas\u2013Houston backbone corridors Zayo is denser and often closest.',
    wholesale: 'Contact: zayo.com/solutions/global-network/ · Zayo wholesale desk',
  },
  Segra: {
    color: '#10b981',
    tier: 'Regional backbone',
    description:
      'Segra is one of the largest independent fiber bandwidth companies in the Eastern US, operating over 43,000 route miles across the Southeast, Mid-Atlantic, and Northeast. Their product suite includes dark fiber, lit wavelengths (10G, 100G+), Ethernet transport, and dedicated internet. Segra is particularly strong in Virginia, the Carolinas, and West Virginia\u2014directly connecting to the Ashburn data center corridor.',
    sourceName: 'Segra UPN Data Map (ArcGIS FeatureServer)',
    sourceUrl: 'https://www.segra.com/locations/',
    sourceCaveat:
      'The public ArcGIS source contains geometry only\u2014no route names, capacity, lit/dark status, or fiber strand count. The service is actively maintained by Segra\u2019s corporate Esri tenant but could go private at any time.',
    btmNote:
      'Segra is the dominant backbone carrier for WV and Appalachian BTM sites. In Harrison County (Wolf Summit corridor), Segra fiber runs within 0.1 miles of candidate parcels. Their Express Optical Core maps Clarksburg as a major connectivity node with direct routing to Ashburn, VA\u2014roughly 155 miles / ~1.2ms one-way fiber latency.',
    wholesale: 'Contact: segra.com/solutions/dark-fiber/ \u00b7 Segra enterprise desk',
  },
};

const NTIA_PROFILE = {
  color: '#e11d48',
  tier: 'NTIA-funded middle mile',
  description:
    'Federally funded fiber route from the NTIA Enabling Middle Mile Broadband Infrastructure Program ($980M nationally). These are new-build or upgraded routes designed to extend backbone connectivity to underserved areas.',
  sourceName: 'NTIA MM Dashboard (ArcGIS FeatureServer)',
  sourceUrl: 'https://broadbandusa.ntia.gov/funding-programs/enabling-middle-mile-broadband-infrastructure-program/award-recipients',
  sourceCaveat:
    'Route geometry is from grant applications. Many routes are under construction (program awarded 2023–2024). Actual as-built paths may differ from planned geometry.',
  btmNote:
    'NTIA middle-mile routes target rural connectivity gaps. Dark fiber leasing potential depends on the awardee’s business model—electric cooperatives and telcos may offer wholesale access, while state agencies may restrict use to last-mile providers.',
  wholesale: 'Contact the awardee directly · broadbandusa.ntia.gov',
};

const NTIA_CARRIER_SET = new Set(['Concho Valley Electric', 'ENMR Telephone', 'Kansas DOC', 'Pima County']);

export const CarrierFiberRouteDetail: React.FC<CarrierFiberRouteDetailProps> = ({ carrier, onClose }) => {
  const { theme } = useTheme();
  const [stats, setStats] = useState<CarrierRouteStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchCarrierRouteStats(controller.signal)
      .then((all) => {
        if (controller.signal.aborted) return;
        const found = all.find((s) => s.carrier === carrier);
        setStats(found || null);
      })
      .catch(() => { /* network errors are non-critical for display */ })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [carrier]);

  const profile = CARRIER_PROFILES[carrier] || (NTIA_CARRIER_SET.has(carrier) ? NTIA_PROFILE : undefined);
  const color = profile?.color || designTokens.colors.textSecondary;
  const ingestDate = stats?.latest_ingest ? new Date(stats.latest_ingest).toLocaleDateString() : null;

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <span style={{
              width: '10px', height: '10px', borderRadius: '2px',
              background: color, flexShrink: 0,
            }} />
            <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>{carrier}</h2>
          </div>
          {profile && (
            <div style={{ fontSize: '10px', color: theme.textSecondary, marginLeft: '18px' }}>
              {profile.tier} \u00b7 Carrier fiber route
            </div>
          )}
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Network stats (from DB) */}
        <Section title="Network in Our Data">
          {loading ? (
            <div style={{ fontSize: '11px', color: theme.textTertiary }}>Loading\u2026</div>
          ) : stats ? (
            <>
              <Field label="Total Segments" value={stats.segments.toLocaleString()} />
              <Field label="Total Route Miles" value={`${Number(stats.total_miles).toLocaleString()} mi`} />
              <Field label="Avg Segment Length" value={`${stats.avg_segment_miles} mi`} />
              {ingestDate && <Field label="Last Ingested" value={ingestDate} />}
            </>
          ) : (
            <div style={{ fontSize: '11px', color: theme.textTertiary, fontStyle: 'italic' }}>
              Stats unavailable.
            </div>
          )}
        </Section>

        {/* Carrier overview */}
        {profile && (
          <Section title="About">
            <div style={{ fontSize: '11px', color: theme.text, lineHeight: 1.55 }}>
              {profile.description}
            </div>
          </Section>
        )}

        {/* BTM / Permian implications */}
        {profile && (
          <Section title="BTM Siting Context">
            <div style={{
              padding: '8px 10px', marginBottom: '6px', borderRadius: '6px',
              background: `${color}10`, border: `1px solid ${color}40`,
            }}>
              <div style={{ fontSize: '11px', color: theme.text, lineHeight: 1.55 }}>
                {profile.btmNote}
              </div>
            </div>
          </Section>
        )}

        {/* Data caveats */}
        <Section title="Data Limitations">
          <div style={{ fontSize: '10px', color: theme.textSecondary, lineHeight: 1.5, marginBottom: '8px' }}>
            Our carrier route datasets contain geometry only — no lit/dark distinction, no strand count, no service type, and no current pricing. Use nearest-distance as a buildout feasibility signal, not a service guarantee.
          </div>
          {profile && (
            <div style={{
              padding: '6px 8px', borderRadius: '4px',
              background: theme.backgroundSecondary, border: `1px solid ${theme.borderLight}`,
              fontSize: '10px', color: theme.textSecondary, lineHeight: 1.5,
            }}>
              <strong style={{ color: theme.text }}>Source caveat:</strong> {profile.sourceCaveat}
            </div>
          )}
        </Section>

        {/* Data source */}
        {profile && (
          <Section title="Data Source">
            <Field label="Dataset" value={profile.sourceName} />
            {stats?.source && <Field label="Ingest Key" value={stats.source} />}
            {profile.sourceUrl && (
              <div style={{ marginTop: '6px' }}>
                <a
                  href={profile.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: '10px', color: color, textDecoration: 'underline' }}
                >
                  {profile.sourceUrl}
                </a>
              </div>
            )}
          </Section>
        )}

        {/* Wholesale / contact */}
        {profile && (
          <Section title="Wholesale / Dark Fiber">
            <div style={{ fontSize: '10px', color: theme.textSecondary, lineHeight: 1.5 }}>
              {profile.wholesale}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};
